import { useState, useEffect } from 'react';
import { Theme } from '../lib/themes';
import KnowledgeGraph from './KnowledgeGraph';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useMinLoading } from '../hooks/useMinLoading';

interface UserFact {
  id: string;
  fact_type: string;
  content: string;
  confidence: number;
  fact_date?: string;
  created_at: string;
}

interface UserFactsPanelProps {
  theme: Theme;
  onClose: () => void;
}

type ViewMode = 'facts' | 'memories' | 'graph';

interface Memory {
  id: string;
  content: string;
  memory_type: string;
  category: string;
  relevance_score: number;
  is_active: boolean;
  usage_count: number;
  last_used_at?: string;
  created_at: string;
}

export default function UserFactsPanel({ theme, onClose }: UserFactsPanelProps) {
  const [facts, setFacts] = useState<UserFact[]>([]);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [factsError, setFactsError] = useState<string | null>(null);
  const [memoriesError, setMemoriesError] = useState<string | null>(null);
  const [isFactsLoading, withFactsLoading] = useMinLoading(500);
  const [isMemoriesLoading, withMemoriesLoading] = useMinLoading(500);
  const [viewMode, setViewMode] = useState<ViewMode>('facts');
  
  // Memory filters and search
  const [memorySearch, setMemorySearch] = useState('');
  const [memoryTypeFilter, setMemoryTypeFilter] = useState<string>('all');
  const [memoryCategoryFilter, setMemoryCategoryFilter] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [sortBy, setSortBy] = useState<'relevance' | 'usage' | 'date'>('relevance');
  const [isDragging, setIsDragging] = useState(false);
  
  // Collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  
  // Delete confirmation modal
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    type: 'memory' | 'fact' | null;
    id: string | null;
    content: string;
  }>({
    show: false,
    type: null,
    id: null,
    content: ''
  });
  
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Load both on mount so counts are accurate
    loadFacts();
    loadMemories();
  }, []);

  const loadFacts = () => withFactsLoading(async () => {
    setFactsError(null);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/memory/facts`);
      const data = await response.json();
      if (data.success) {
        setFacts(data.facts);
      } else {
        setFactsError('Failed to load medical facts.');
      }
    } catch (error) {
      console.error('Failed to load facts:', error);
      setFactsError('Could not connect to the server. Check your connection and try again.');
    }
  });

  const loadMemories = () => withMemoriesLoading(async () => {
    setMemoriesError(null);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/memory/memories`);
      const data = await response.json();
      if (data.success) {
        setMemories(data.memories);
      } else {
        setMemoriesError('Failed to load memories.');
      }
    } catch (error) {
      console.error('Failed to load memories:', error);
      setMemoriesError('Could not connect to the server. Check your connection and try again.');
    }
  });

  const deleteFact = async (factId: string) => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/memory/facts/${factId}`, {
        method: 'DELETE',
      });
      setFacts(facts.filter(f => f.id !== factId));
      setDeleteConfirmation({ show: false, type: null, id: null, content: '' });
    } catch (error) {
      console.error('Failed to delete fact:', error);
    }
  };

  const updateFactConfidence = async (factId: string, confidence: number) => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/memory/facts/${factId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confidence }),
      });
    } catch (error) {
      console.error('Failed to update fact:', error);
    }
  };

  const handleFactConfidenceChange = (factId: string, confidence: number) => {
    // Update locally immediately for smooth UI
    setFacts(facts.map(f => f.id === factId ? { ...f, confidence } : f));
  };

  const handleFactConfidenceCommit = (factId: string, confidence: number) => {
    // Save to server when slider is released
    updateFactConfidence(factId, confidence);
  };

  const deleteMemory = async (memoryId: string) => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/memory/memories/${memoryId}`, {
        method: 'DELETE',
      });
      setMemories(memories.filter(m => m.id !== memoryId));
      setDeleteConfirmation({ show: false, type: null, id: null, content: '' });
    } catch (error) {
      console.error('Failed to delete memory:', error);
    }
  };
  
  const showDeleteConfirmation = (type: 'memory' | 'fact', id: string, content: string) => {
    setDeleteConfirmation({ show: true, type, id, content });
  };
  
  const handleDeleteConfirm = () => {
    if (deleteConfirmation.type === 'memory' && deleteConfirmation.id) {
      deleteMemory(deleteConfirmation.id);
    } else if (deleteConfirmation.type === 'fact' && deleteConfirmation.id) {
      deleteFact(deleteConfirmation.id);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmation({ show: false, type: null, id: null, content: '' });
  };

  const updateMemory = async (memoryId: string, updates: { relevance_score?: number; is_active?: boolean }) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/memory/memories/${memoryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      
      if (!data.success) {
        console.error('Failed to update memory');
      }
    } catch (error) {
      console.error('Failed to update memory:', error);
    }
  };

  const handleMemoryRelevanceChange = (memoryId: string, relevance_score: number) => {
    // Update locally immediately for smooth UI
    setIsDragging(true);
    setMemories(memories.map(m => m.id === memoryId ? { ...m, relevance_score } : m));
  };

  const handleMemoryRelevanceCommit = (memoryId: string, relevance_score: number) => {
    // Save to server when slider is released
    setIsDragging(false);
    updateMemory(memoryId, { relevance_score });
  };

  const toggleMemoryActive = (memoryId: string, is_active: boolean) => {
    // Update locally and save immediately for toggle
    setMemories(memories.map(m => m.id === memoryId ? { ...m, is_active } : m));
    updateMemory(memoryId, { is_active });
  };

  // Filter and sort memories - disable sorting while dragging to prevent reordering
  const filteredMemories = [...memories]
    .filter(m => {
      if (memorySearch && !m.content.toLowerCase().includes(memorySearch.toLowerCase())) {
        return false;
      }
      if (memoryTypeFilter !== 'all' && m.memory_type !== memoryTypeFilter) {
        return false;
      }
      if (memoryCategoryFilter !== 'all' && m.category !== memoryCategoryFilter) {
        return false;
      }
      if (!showInactive && !m.is_active) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      // Don't sort while dragging to prevent cards from jumping around
      if (isDragging) return 0;
      
      if (sortBy === 'relevance') {
        return b.relevance_score - a.relevance_score;
      } else if (sortBy === 'usage') {
        return b.usage_count - a.usage_count;
      } else {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  const memoryTypes = Array.from(new Set(memories.map(m => m.memory_type)));
  const memoryCategories = Array.from(new Set(memories.map(m => m.category)));

  const getMemoryIcon = (type: string) => {
    const icons: Record<string, string> = {
      instruction: '📝',
      preference: '⭐',
      learning: '🧠',
      correction: '✏️',
    };
    return icons[type] || '💭';
  };

  const factsByType = facts.reduce((acc, fact) => {
    if (!acc[fact.fact_type]) {
      acc[fact.fact_type] = [];
    }
    acc[fact.fact_type].push(fact);
    return acc;
  }, {} as Record<string, UserFact[]>);

  const formatFactType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getFactIcon = (type: string) => {
    const icons: Record<string, string> = {
      medical_condition: '🏥',
      allergy: '⚠️',
      medication: '💊',
      procedure: '🔬',
      family_history: '👨‍👩‍👧‍👦',
      lifestyle: '🏃',
      preference: '⭐',
    };
    return icons[type] || '📋';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return theme.colors.primary;
    if (confidence >= 0.7) return '#10b981';
    return '#f59e0b';
  };

  const getRelevanceColor = (relevance: number) => {
    if (relevance >= 0.9) return theme.colors.primary;
    if (relevance >= 0.7) return '#10b981';
    return '#f59e0b';
  };

  return (
    <div className="flex flex-col h-full pb-16 lg:pb-0">
      {/* Tabs */}
      <div className="flex flex-shrink-0 mb-4" style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
        {([
          { mode: 'graph' as ViewMode, icon: '🕸️', label: 'Knowledge Graph', count: null, loading: false },
          { mode: 'facts' as ViewMode, icon: '📋', label: 'Medical Facts', count: isFactsLoading ? null : facts.length, loading: isFactsLoading },
          { mode: 'memories' as ViewMode, icon: '🧠', label: 'Memories', count: isMemoriesLoading ? null : memories.length, loading: isMemoriesLoading },
        ] as { mode: ViewMode; icon: string; label: string; count: number | null; loading: boolean }[]).map(({ mode, icon, label, count, loading }) => {
          const isActive = viewMode === mode;
          return (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className="flex-1 flex flex-col items-center gap-1 pt-3 pb-5 relative transition-all"
              style={{ color: isActive ? theme.colors.primary : theme.colors.textSecondary }}
            >
              <span className="text-xl leading-none">{icon}</span>
              <span className="text-xs font-medium leading-tight">{label}</span>
              {/* Fixed-height count row — prevents layout shift */}
              <span className="h-4 flex items-center justify-center">
                {loading ? (
                  <span
                    className="inline-block w-8 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: theme.colors.border }}
                  />
                ) : count !== null ? (
                  <span
                    className="text-xs leading-none opacity-50"
                    style={{ color: isActive ? theme.colors.primary : theme.colors.textSecondary }}
                  >
                    {count.toLocaleString()}
                  </span>
                ) : (
                  /* graph tab — keep height consistent */
                  <span className="inline-block w-8 h-2 rounded-full opacity-0" />
                )}
              </span>
              {isActive && (
                <div className="absolute bottom-0 left-6 right-6 h-0.5 rounded-full" style={{ backgroundColor: theme.colors.primary }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Memory Filters - Fixed (only for memories view) */}
      {viewMode === 'memories' && !isMemoriesLoading && memories.length > 0 && (
        <div className="px-6 pt-4 pb-3 border-b flex-shrink-0" style={{ borderColor: theme.colors.border }}>
          <div className="max-w-5xl mx-auto space-y-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search memories..."
                value={memorySearch}
                onChange={(e) => setMemorySearch(e.target.value)}
                className="w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                }}
              />
              <svg className="absolute right-3 top-2.5 w-5 h-5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={memoryTypeFilter}
                onChange={(e) => setMemoryTypeFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs focus:outline-none"
                style={{
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <option value="all">All Types</option>
                {memoryTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              
              <select
                value={memoryCategoryFilter}
                onChange={(e) => setMemoryCategoryFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs focus:outline-none"
                style={{
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <option value="all">All Categories</option>
                {memoryCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'relevance' | 'usage' | 'date')}
                className="px-3 py-1.5 rounded-lg text-xs focus:outline-none"
                style={{
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <option value="relevance">Sort by Relevance</option>
                <option value="usage">Sort by Usage</option>
                <option value="date">Sort by Date</option>
              </select>
              
              <button
                onClick={() => setShowInactive(!showInactive)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: showInactive ? theme.colors.primary + '20' : theme.colors.background,
                  color: showInactive ? theme.colors.primary : theme.colors.textSecondary,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                {showInactive ? '👁️ Showing Inactive' : '👁️ Active Only'}
              </button>
              
              <div className="text-xs opacity-60 ml-auto">
                Showing {filteredMemories.length} of {memories.length} memories
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {viewMode === 'graph' ? (
          <KnowledgeGraph userId="user-1" theme={theme} />
        ) : (
          <div className="max-w-5xl mx-auto p-6">
            {(viewMode === 'facts' ? isFactsLoading : viewMode === 'memories' ? isMemoriesLoading : false) ? (
              /* Skeleton — matches the section+card layout so nothing jumps on load */
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i}>
                    {/* Section header skeleton */}
                    <div
                      className="flex items-center justify-between p-3 rounded-lg mb-3 animate-pulse"
                      style={{ backgroundColor: theme.colors.surface }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md" style={{ backgroundColor: theme.colors.border }} />
                        <div className="w-28 h-4 rounded-full" style={{ backgroundColor: theme.colors.border }} />
                        <div className="w-8 h-3 rounded-full opacity-50" style={{ backgroundColor: theme.colors.border }} />
                      </div>
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: theme.colors.border }} />
                    </div>
                    {/* Card skeletons */}
                    <div className="space-y-2 pl-2">
                      {[1, 2].map(j => (
                        <div
                          key={j}
                          className="p-4 rounded-xl animate-pulse"
                          style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}` }}
                        >
                          <div className="w-full h-3 rounded-full mb-2" style={{ backgroundColor: theme.colors.border }} />
                          <div className="w-3/4 h-3 rounded-full mb-4" style={{ backgroundColor: theme.colors.border }} />
                          <div className="flex items-center gap-3">
                            <div className="w-16 h-2 rounded-full opacity-50" style={{ backgroundColor: theme.colors.border }} />
                            <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: theme.colors.border }} />
                            <div className="w-8 h-2 rounded-full opacity-50" style={{ backgroundColor: theme.colors.border }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : viewMode === 'facts' && factsError ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="text-5xl mb-4">⚠️</div>
                <p className="text-base font-semibold mb-2" style={{ color: theme.colors.text }}>Failed to load medical facts</p>
                <p className="text-sm mb-6" style={{ color: theme.colors.textSecondary }}>{factsError}</p>
                <button
                  onClick={loadFacts}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                >
                  Retry
                </button>
              </div>
            ) : viewMode === 'memories' && memoriesError ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="text-5xl mb-4">⚠️</div>
                <p className="text-base font-semibold mb-2" style={{ color: theme.colors.text }}>Failed to load memories</p>
                <p className="text-sm mb-6" style={{ color: theme.colors.textSecondary }}>{memoriesError}</p>
                <button
                  onClick={loadMemories}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                >
                  Retry
                </button>
              </div>
            ) : viewMode === 'facts' ? (
              facts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🤔</div>
                  <p className="text-lg mb-2" style={{ color: theme.colors.text }}>
                    No facts extracted yet
                  </p>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                    Have a conversation about your medical history to build your profile
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(factsByType).map(([type, typeFacts]) => (
                    <div key={type}>
                      <button
                        onClick={() => toggleSection(type)}
                        className="flex items-center justify-between w-full text-left hover:opacity-80 transition-opacity mb-3 p-3 rounded-lg"
                        style={{ backgroundColor: theme.colors.surface }}
                      >
                        <h3 className="flex items-center gap-2 text-lg font-semibold" style={{ color: theme.colors.text }}>
                          <span className="text-2xl">{getFactIcon(type)}</span>
                          {formatFactType(type)}
                          <span className="text-sm font-normal opacity-60">({typeFacts.length})</span>
                        </h3>
                        <svg
                          className={`w-5 h-5 transition-transform ${collapsedSections.has(type) ? '' : 'rotate-180'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ color: theme.colors.textSecondary }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {!collapsedSections.has(type) && (
                        <div className="space-y-2 pl-2">
                          {typeFacts.map((fact) => (
                            <div
                              key={fact.id}
                              className="p-4 rounded-xl group"
                              style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}` }}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm mb-3" style={{ color: theme.colors.text }}>
                                    {fact.content}
                                  </p>
                                  
                                  {/* Confidence Slider */}
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs opacity-60 flex-shrink-0 w-20">Confidence:</span>
                                    <div className="flex-1 relative">
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={fact.confidence}
                                        onInput={(e) => handleFactConfidenceChange(fact.id, parseFloat((e.target as HTMLInputElement).value))}
                                        onMouseUp={(e) => handleFactConfidenceCommit(fact.id, parseFloat((e.target as HTMLInputElement).value))}
                                        onTouchEnd={(e) => handleFactConfidenceCommit(fact.id, parseFloat((e.target as HTMLInputElement).value))}
                                        className="w-full"
                                        style={{
                                          background: `linear-gradient(to right, ${getConfidenceColor(fact.confidence)} 0%, ${getConfidenceColor(fact.confidence)} ${fact.confidence * 100}%, ${theme.colors.border} ${fact.confidence * 100}%, ${theme.colors.border} 100%)`,
                                          color: getConfidenceColor(fact.confidence),
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs font-medium flex-shrink-0 w-12 text-right" style={{ color: getConfidenceColor(fact.confidence) }}>
                                      {(fact.confidence * 100).toFixed(0)}%
                                    </span>
                                  </div>
                                  
                                  {fact.fact_date && (
                                    <p className="text-xs mt-2 opacity-60">
                                      📅 {new Date(fact.fact_date).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={() => showDeleteConfirmation('fact', fact.id, fact.content)}
                                  className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-red-500/20 transition-all flex-shrink-0"
                                  style={{ color: '#ef4444' }}
                                  title="Delete fact"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Memories View
              filteredMemories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <p className="text-lg mb-2" style={{ color: theme.colors.text }}>
                    {memories.length === 0 ? 'No memories stored yet' : 'No memories found'}
                  </p>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                    {memorySearch || memoryTypeFilter !== 'all' || memoryCategoryFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Memories are learnings extracted from conversations'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMemories.map((memory) => (
                    <div
                      key={memory.id}
                      className="p-4 rounded-xl group transition-opacity"
                      style={{ 
                        backgroundColor: theme.colors.surface,
                        opacity: memory.is_active ? 1 : 0.5,
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-lg">{getMemoryIcon(memory.memory_type)}</span>
                          <span className="text-xs px-2 py-1 rounded-full" style={{ 
                            backgroundColor: theme.colors.primary + '20',
                            color: theme.colors.primary 
                          }}>
                            {memory.memory_type}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full" style={{ 
                            backgroundColor: theme.colors.background,
                            color: theme.colors.textSecondary 
                          }}>
                            {memory.category}
                          </span>
                          {memory.usage_count > 0 && (
                            <span className="text-xs opacity-60">
                              🔄 {memory.usage_count}x
                            </span>
                          )}
                        </div>
                        
                        {/* Controls */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => toggleMemoryActive(memory.id, !memory.is_active)}
                            className="p-2 rounded-lg hover:bg-white/10 transition-all"
                            style={{ color: memory.is_active ? theme.colors.primary : theme.colors.textSecondary }}
                            title={memory.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={memory.is_active ? "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" : "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"} />
                            </svg>
                          </button>
                          <button
                            onClick={() => showDeleteConfirmation('memory', memory.id, memory.content)}
                            className="p-2 rounded-lg hover:bg-red-500/20 transition-all"
                            style={{ color: '#ef4444' }}
                            title="Delete memory"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <p className="text-sm mb-3" style={{ color: theme.colors.text }}>
                        {memory.content}
                      </p>
                      
                      {/* Relevance Slider */}
                      <div className="flex items-center gap-3">
                        <span className="text-xs opacity-60 flex-shrink-0 w-20">Relevance:</span>
                        <div className="flex-1 relative">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={memory.relevance_score}
                            onInput={(e) => handleMemoryRelevanceChange(memory.id, parseFloat((e.target as HTMLInputElement).value))}
                            onMouseUp={(e) => handleMemoryRelevanceCommit(memory.id, parseFloat((e.target as HTMLInputElement).value))}
                            onTouchEnd={(e) => handleMemoryRelevanceCommit(memory.id, parseFloat((e.target as HTMLInputElement).value))}
                            className="w-full"
                            style={{
                              background: `linear-gradient(to right, ${getRelevanceColor(memory.relevance_score)} 0%, ${getRelevanceColor(memory.relevance_score)} ${memory.relevance_score * 100}%, ${theme.colors.border} ${memory.relevance_score * 100}%, ${theme.colors.border} 100%)`,
                              color: getRelevanceColor(memory.relevance_score),
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium flex-shrink-0 w-12 text-right" style={{ color: getRelevanceColor(memory.relevance_score) }}>
                          {(memory.relevance_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      {viewMode !== 'graph' && (
        <div className="p-4 border-t flex-shrink-0" style={{ borderColor: theme.colors.border }}>
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-xs" style={{ color: theme.colors.textSecondary }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {viewMode === 'facts' 
                  ? 'Adjust confidence sliders to control how much weight each fact has. Facts below 50% are excluded from AI context.'
                  : 'Adjust relevance to control memory importance. Toggle visibility or delete memories you don\'t want used.'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={deleteConfirmation.show}
        title={`Delete ${deleteConfirmation.type === 'memory' ? 'Memory' : 'Fact'}?`}
        message={`This action cannot be undone. The ${deleteConfirmation.type} will be permanently removed.`}
        itemPreview={deleteConfirmation.content}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        theme={theme}
      />
    </div>
  );
}
